/**
 * Serialization helpers for inquiry wizard drafts.
 *
 * Versioning contract
 * -------------------
 * Every saved draft contains a `version` integer set to DRAFT_SCHEMA_VERSION
 * at the time it was created.  When restoring a draft, `migrateDraft()` walks
 * through the `migrations` map and upgrades the payload one version at a time
 * until it matches the current schema.
 *
 * How to make a breaking change to the serialized shape:
 *   1. Increment DRAFT_SCHEMA_VERSION.
 *   2. Add an entry in `migrations` keyed by the FROM version
 *      (i.e. the version that needs to be upgraded).
 *   3. The function receives the old data object and must return an updated
 *      copy that satisfies the new schema.
 */

/** Current serialized schema version.  Bump when the shape changes. */
export const DRAFT_SCHEMA_VERSION = 1;

/**
 * Migration functions keyed by the version they upgrade FROM.
 * e.g. migrations[0] upgrades a version-0 (un-versioned) draft to version 1.
 */
const migrations = {
  /**
   * 0 → 1: first versioned format.
   * Fills in fields that were absent in drafts saved before versioning was
   * introduced, ensuring a consistent shape for all later code paths.
   */
  0: (data) => ({
    ...data,
    questionAiAnalysis: data.questionAiAnalysis ?? {},
    surveyRevisionCreationLimitInDays: data.surveyRevisionCreationLimitInDays ?? null,
  }),
};

/**
 * Runs all migrations needed to bring `data` up to DRAFT_SCHEMA_VERSION.
 * Returns a new object; the original is never mutated.
 *
 * Drafts saved before versioning was introduced have no `version` field and
 * are treated as version 0.
 *
 * @param {object} data  Raw parsed draft payload
 * @returns {object}     Migrated payload at DRAFT_SCHEMA_VERSION
 */
export function migrateDraft(data) {
  let version = data.version ?? 0;
  let migrated = { ...data };

  while (version < DRAFT_SCHEMA_VERSION) {
    const migrate = migrations[version];
    if (!migrate) {
      // No migration defined for this step; just advance the counter so we
      // don't loop forever if a version was skipped during development.
      version += 1;
      continue;
    }
    migrated = migrate(migrated);
    version += 1;
  }

  return { ...migrated, version: DRAFT_SCHEMA_VERSION };
}

/**
 * Converts a `File` object to a plain, JSON-safe object for serialization.
 *
 * @param {File} file
 * @returns {Promise<{name: string, size: number, type: string, lastModified: number, base64Content: string}>}
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve({
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        base64Content: reader.result.split(',')[1], // strip the data-URL prefix
      });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Reconstructs a `File` object from a base64-encoded plain object produced by
 * `fileToBase64`.
 *
 * @param {{name: string, type: string, lastModified: number, base64Content: string}} obj
 * @returns {File}
 */
export function base64ToFile(obj) {
  const byteString = atob(obj.base64Content);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new File([ab], obj.name, { type: obj.type, lastModified: obj.lastModified });
}
